import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { Option } from 'src/app/components/select/select.component';
import { Campaign, CampaignSchedule, CampaignScheduleValues, CampaignType, } from 'src/app/models/campaign';
import { Case } from 'src/app/models/case';
import { Organization } from 'src/app/models/organization';
import { Paginator } from 'src/app/models/paginator';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { OrganizationService } from 'src/app/services/organization.service';
import { TagService } from 'src/app/services/tag.service';

@Component({
  selector: 'app-create-campaign-dialog',
  templateUrl: './create-campaign-dialog.component.html',
  styleUrl: './create-campaign-dialog.component.scss'
})
export class CreateCampaignDialogComponent {
  organization?: Organization;

  user?: User;

  mode: "create" | "edit" = "create";

  campaignToCreate: Partial<Campaign> = {
    name: undefined,
    type: undefined,
    schedule: undefined,
    targets: undefined,
    entity: {
      type: "none",
      ref: undefined
    }
  };

  userTargets: {
    users: {
      ref: string;
      firstName: string;
      lastName: string;
      avatar: string;
      email: string;
    }[],
    length: number
  } = {
      users: [],
      length: 0
    };

  caseTarget?: Case;

  territoryTarget?: string;

  types: Option[] = [
    {
      value: 'CASE',
      label: 'CAMPAIGN.TYPE_CASE',
      selected: false
    },
    {
      value: 'COUNTRY',
      label: 'CAMPAIGN.TYPE_COUNTRY',
      selected: false
    },
    {
      value: 'OPERATORS',
      label: 'CAMPAIGN.TYPE_OPERATORS',
      selected: false
    },
    {
      value: 'CASE_OPERATORS',
      label: 'CAMPAIGN.TYPE_CASE_OPERATORS',
      selected: false
    },
    {
      value: 'COUNTRY_OPERATORS',
      label: 'CAMPAIGN.TYPE_COUNTRY_OPERATORS',
      selected: false
    },
    {
      value: 'INDIVIDUALS',
      label: 'CAMPAIGN.TYPE_INDIVIDUALS',
      selected: false
    }
  ];

  schedules: {
    value: CampaignSchedule,
    label: string,
    selected: boolean,
    disabled: boolean
  }[] = [
      {
        value: CampaignScheduleValues[5],
        label: "DATES.ONCE",
        selected: false,
        disabled: false
      },
      {
        value: CampaignScheduleValues[4],
        label: "DATES.WEEKLY",
        selected: false,
        disabled: false
      },
      {
        value: CampaignScheduleValues[3],
        label: "DATES.MONTHLY",
        selected: false,
        disabled: false
      },
      {
        value: CampaignScheduleValues[2],
        label: "DATES.QUARTERLY",
        selected: false,
        disabled: false
      },
      {
        value: CampaignScheduleValues[1],
        label: "DATES.SEMESTERLY",
        selected: false,
        disabled: false
      },
      {
        value: CampaignScheduleValues[0],
        label: "DATES.YEARLY",
        selected: false,
        disabled: false
      }
    ];

  entities: {
    value: "none" | "trademark" | "trademarkFamily",
    label: string,
    selected: boolean,
    disabled: boolean
  }[] = [
      {
        value: "none",
        label: "CAMPAIGN.NONE",
        selected: true,
        disabled: false
      },
      {
        value: "trademark",
        label: "ENTITIES.TRADEMARK",
        selected: false,
        disabled: false
      },
      {
        value: "trademarkFamily",
        label: "ENTITIES.TRADEMARK_FAMILY",
        selected: false,
        disabled: false
      }
    ];

  userOptions: { value: any, label: string, disabled: boolean, selected: boolean }[] = [];

  errors: {
    [key: string]: {
      value: boolean,
      message?: string
    }
  } = {
      name: {
        value: false,
        message: undefined
      },
      type: {
        value: false,
        message: undefined
      },
      schedule: {
        value: false,
        message: undefined
      },
      targets: {
        value: false,
        message: undefined
      }
    }

  private readonly labelSubject = new Subject<string>();
  private labelSubscription?: Subscription;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthenticationService,
    private dialogRef: MatDialogRef<CreateCampaignDialogComponent>,
    private organizationService: OrganizationService,
    private translate: TranslateService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
    this.labelSubscription = this.labelSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(async () => {
        // await this.exists();
      });
  }

  async ngOnInit() {
    this.organization = this.data.organization;
    await this.retrieveUsers();
    if (this.data) {
      this.mode = this.data.mode;
      if (this.mode == 'edit') {
        this.campaignToCreate = JSON.parse(JSON.stringify(this.data.campaign));
        if (this.campaignToCreate.type === CampaignType.CASE && this.campaignToCreate.targets && this.campaignToCreate.targets.length === 1) {
          this.caseTarget = {
            _id: (this.campaignToCreate.targets[0] as any).ref,
            reference: (this.campaignToCreate.targets[0] as any).reference,
            trademark: (this.campaignToCreate.targets[0] as any).trademark,
          } as Case
        }
        if (this.campaignToCreate.type === CampaignType.COUNTRY && this.campaignToCreate.targets && this.campaignToCreate.targets.length === 1) {
          this.territoryTarget = (this.campaignToCreate.targets[0] as any).id
        }
        if (this.campaignToCreate.type === CampaignType.INDIVIDUALS && this.campaignToCreate.targets && this.campaignToCreate.targets.length > 0) {
          const userIds = this.campaignToCreate.targets.map((target) => (target as any).ref);
          this.userTargets = {
            users: this.campaignToCreate.targets as any[],
            length: this.campaignToCreate.targets.length
          }
          this.userOptions.forEach((option) => {
            if (userIds.includes(option.value._id)) {
              option.selected = true;
            }
          })
        }
      }
    }
  }

  ngOnDestroy(): void {
  }

  async getUsersByRole() {
    const ROLES = {
      'OPERATORS': 'Operator',
      'CASE_OPERATORS': 'Case Operator',
      'COUNTRY_OPERATORS': 'Country Operator',
      'CASE': undefined,
      'COUNTRY': undefined,
      'INDIVIDUALS': undefined
    };
    if (this.campaignToCreate && this.campaignToCreate.type && ['OPERATORS', 'CASE_OPERATORS', 'COUNTRY_OPERATORS'].includes(this.campaignToCreate.type)) {
      const role = ROLES[this.campaignToCreate.type];
      if (role) {
        const users = await this.organizationService.retrieveUsersByRole([role]);
        const mappedUsers = users.map((user: any) => ({
          ref: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          avatar: user.avatar,
          email: user.email
        }));
        this.userTargets = {
          users: mappedUsers,
          length: mappedUsers.length
        }
        this.campaignToCreate.targets = mappedUsers;
      }

    }

  }

  async retrieveUsers() {
    const operators = await this.organizationService.retrieveUsersByRole(['Operator', 'Country Operator', 'Case Operator']);
    this.userOptions = operators.map(user => ({ value: user, selected: false, disabled: false, label: `${user.lastName} ${user.firstName}` }));
  }

  onTypeChange(event: any) {
    if (event && event.value && event.value.value && ['OPERATORS', 'CASE_OPERATORS', 'COUNTRY_OPERATORS'].includes(event.value.value)) {
      this.campaignToCreate.type = event.value.value;
      this.getUsersByRole();
    }
  }

  onCaseChange(event: any) {
    if (event && event.value && event.value.id) {
      const caseObject = event.value;
      this.caseTarget = caseObject;
      if (caseObject.state === 'CLOSED') {
        this.errors['targets'].value = true;
        this.errors['targets'].message = this.translate.instant('ERRORS.CAMPAIGN_TARGETS_CASE_CLOSED');
      } else {
        this.campaignToCreate.targets = [{
          ref: caseObject._id,
          reference: caseObject.reference,
          trademark: caseObject.trademark
        }]
      }

    }
  }

  onTerritoryChange(event: any) {
    if (event && event.value && event.value.id) {
      this.campaignToCreate.targets = [{
        id: event.value.id,
        code: event.value.code
      }];
      this.territoryTarget = event.value.id;
    }
  }

  onUsersChange(event: any) {
    if (event && event.value) {
      const users = event.value.map((user: any) => ({
        ref: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        avatar: user.avatar,
        email: user.email
      }))
      this.campaignToCreate.targets = users;
      this.userTargets = {
        users: users,
        length: users.length
      }
    }
  }

  onScheduleChange(event: any) {
    if (event && event.value && event.value.value) {
      this.campaignToCreate.schedule = event.value.value;
    }
  }

  isDisabled() {
    // TODO: check existence of campaign for current to be created (exists method)
    return !this.campaignToCreate.name || this.errors['name'].value
      || !this.campaignToCreate.type || this.errors['type'].value
      || !this.campaignToCreate.schedule || this.errors['schedule'].value
      || !this.campaignToCreate.targets || this.errors['targets'].value
      || (this.campaignToCreate.type === CampaignType.CASE && (!this.caseTarget?.state || this.caseTarget?.state === 'CLOSED'));
  }

  _hasErrors() {
    this.errors['label'] = {
      value: false,
      message: undefined
    }
    if (!this.campaignToCreate.name) {
      this.errors['name'].value = true;
      this.errors['name'].message = this.translate.instant('ERRORS.CAMPAIGN_NAME_REQUIRED');
    }

    this.errors['type'] = {
      value: false,
      message: undefined
    }
    if (!this.campaignToCreate.type) {
      this.errors['type'].value = true;
      this.errors['type'].message = this.translate.instant('ERRORS.CAMPAIGN_TYPE_REQUIRED');
    }

    this.errors['schedule'] = {
      value: false,
      message: undefined
    }
    if (!this.campaignToCreate.schedule) {
      this.errors['schedule'].value = true;
      this.errors['schedule'].message = this.translate.instant('ERRORS.CAMPAIGN_SCHEDULE_REQUIRED');
    }

    this.errors['targets'] = {
      value: false,
      message: undefined
    }
    if (!this.campaignToCreate.targets || this.campaignToCreate.targets.length === 0) {
      this.errors['targets'].value = true;
      this.errors['targets'].message = this.translate.instant('ERRORS.CAMPAIGN_TARGETS_REQUIRED');
    } else if (this.caseTarget && this.caseTarget.state === 'CLOSED') {
      this.errors['targets'].value = true;
      this.errors['targets'].message = this.translate.instant('ERRORS.CAMPAIGN_TARGETS_CASE_CLOSED');
    }


    return this.errors['name'].value || this.errors['type'].value || this.errors['schedule'].value || this.errors['targets'].value;
  }

  onCancel(): void {
    this.dialogRef.close({
      created: false,
      edited: false
    });
  }

  async onCreate() {
    if (this._hasErrors()) {
      return
    } else {
      this.dialogRef.close({
        created: true,
        campaign: this.campaignToCreate
      });
    }
  }

  async onEdit() {
    if (this._hasErrors()) {
      return
    } else {
      this.dialogRef.close({
        edited: true,
        campaign: this.campaignToCreate
      });
    }
  }

  onEntityTypeChange(event: any) {
    if (event && event.value && event.value.value && this.campaignToCreate && this.campaignToCreate.entity) {
      this.campaignToCreate.entity.type = event.value.value;
    }
  }

  onEntityRefChange(event: any) {
    if (event && event.value && this.campaignToCreate && this.campaignToCreate.entity) {
      this.campaignToCreate.entity.ref = event.value._id;
      if (this.campaignToCreate.entity.type === 'trademark') {
        this.campaignToCreate.entity.label = `${event.value.name || event.value.identifierNumber} (${!event.value.name ? '' : event.value.identifierNumber + ' '}${event.value.countryOfDesignation})`
      } else if (this.campaignToCreate.entity.type === 'trademarkFamily') {
        this.campaignToCreate.entity.label = event.value.label;
      }
    }
  }
}
